import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'

/**
 * Response from AV8 API autocomplete action
 */
export interface AircraftAutocompleteResult {
  id: number
  manufacturer: string
  model: string
  variant?: string
  category?: string
  icao_designator?: string
}

/**
 * Full aircraft data from AV8 API get action
 */
export interface ExternalAircraftData {
  id: number
  manufacturer: string
  model: string
  variant?: string
  category?: string
  icao_designator?: string
  // Dimensions (may be in different units - API uses meters, we need feet)
  length?: number
  wingspan?: number
  height?: number
  tail_height?: number
  // Weights (may be in different units - API uses kg, we need lbs)
  empty_weight?: number
  max_takeoff_weight?: number
  max_ramp_weight?: number
  // Landing gear
  gear_type?: 'tricycle' | 'conventional' | 'tandem'
  main_gear_wheels?: number
  nose_gear_steering_angle?: number
  nose_gear_from_nose?: number
  main_gear_from_nose?: number
  wheel_track?: number
  wheelbase?: number
  turning_radius?: number
}

interface AircraftSearchApiResponse {
  status?: boolean
  error?: string
  data?: AircraftAutocompleteResult[]
}

interface AircraftGetApiResponse {
  status?: boolean
  error?: string
  data?: ExternalAircraftData
}

/**
 * Search for aircraft using the AV8 API via edge function
 */
async function searchAircraft(
  query: string,
  limit: number = 10,
  fullData: boolean = false
): Promise<AircraftAutocompleteResult[]> {
  if (!query || query.length < 2) {
    return []
  }

  const { data, error } = await supabase.functions.invoke<AircraftSearchApiResponse>(
    'aircraft-search',
    {
      body: {
        action: 'autocomplete',
        query,
        limit,
        full_data: fullData,
      },
    }
  )

  if (error) {
    console.error('Aircraft search error:', error)
    throw new Error(error.message || 'Failed to search aircraft')
  }

  if (data?.error) {
    throw new Error(data.error)
  }

  return data?.data || []
}

/**
 * Get full aircraft details by external API ID
 */
async function getAircraftDetails(id: number): Promise<ExternalAircraftData | null> {
  const { data, error } = await supabase.functions.invoke<AircraftGetApiResponse>(
    'aircraft-search',
    {
      body: {
        action: 'get',
        id: String(id),
      },
    }
  )

  if (error) {
    console.error('Aircraft get error:', error)
    throw new Error(error.message || 'Failed to get aircraft details')
  }

  if (data?.error) {
    throw new Error(data.error)
  }

  return data?.data || null
}

/**
 * Custom hook for debounced input value
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Query key factory for aircraft search
 */
export const aircraftSearchKeys = {
  all: ['aircraftSearch'] as const,
  autocomplete: (query: string) => [...aircraftSearchKeys.all, 'autocomplete', query] as const,
  details: (id: number) => [...aircraftSearchKeys.all, 'details', id] as const,
}

/**
 * Hook for aircraft autocomplete search with debouncing
 * @param query - Search query string
 * @param options - Configuration options
 */
export function useAircraftAutocomplete(
  query: string,
  options: {
    debounceMs?: number
    limit?: number
    enabled?: boolean
  } = {}
) {
  const { debounceMs = 300, limit = 10, enabled = true } = options
  const debouncedQuery = useDebounce(query, debounceMs)

  return useQuery({
    queryKey: aircraftSearchKeys.autocomplete(debouncedQuery),
    queryFn: () => searchAircraft(debouncedQuery, limit),
    enabled: enabled && debouncedQuery.length >= 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  })
}

/**
 * Hook for fetching full aircraft details by external API ID
 * @param id - External API aircraft ID
 */
export function useAircraftDetails(id: number | null) {
  return useQuery({
    queryKey: aircraftSearchKeys.details(id || 0),
    queryFn: () => getAircraftDetails(id!),
    enabled: id !== null && id > 0,
    staleTime: 1000 * 60 * 30, // 30 minutes - aircraft data doesn't change often
  })
}

/**
 * Hook for manual aircraft search (non-reactive)
 * Returns a function to trigger search on demand
 */
export function useAircraftSearchFn() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const search = useCallback(async (query: string, limit: number = 10) => {
    setIsLoading(true)
    setError(null)

    try {
      const results = await searchAircraft(query, limit)
      return results
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed'
      setError(message)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getDetails = useCallback(async (id: number) => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await getAircraftDetails(id)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get details'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    search,
    getDetails,
    isLoading,
    error,
  }
}
